import React, { useState } from 'react';
import './TraceModeSelector.css';

function TraceModeSelector({ onModeChange, loading }) {
  const [selectedMode, setSelectedMode] = useState('smart');
  const [maxDepth, setMaxDepth] = useState(5);
  const [numPaths, setNumPaths] = useState(3);
  const [branches, setBranches] = useState(2);
  const [maxPaths, setMaxPaths] = useState(20);
  const [useWalletExplorer, setUseWalletExplorer] = useState(false);

  const modes = [
    {
      id: 'smart',
      name: 'Quick Trace',
      icon: '⚡',
      description: 'Folgt dem wahrscheinlichsten Pfad (schnell)',
      speed: 'Sehr schnell',
      color: '#4CAF50'
    },
    {
      id: 'multi',
      name: 'Multiple Sources',
      icon: '🔀',
      description: 'Top N wahrscheinlichste Quellen',
      speed: 'Schnell',
      color: '#2196F3'
    },
    {
      id: 'full',
      name: 'Full Graph',
      icon: '🕸️',
      description: 'Alle Inputs rekursiv (Graph)',
      speed: 'Mittel',
      color: '#FF9800',
      disabled: true,
      disabledReason: 'Wird in v0.4 mit Background-Support verbessert'
    },
    {
      id: 'tree',
      name: 'Deep Analysis',
      icon: '🌳',
      description: 'Verzweigt auf jeder Ebene (langsam!)',
      speed: 'Langsam',
      color: '#F44336'
    }
  ];

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    
    const modeConfig = {
      mode,
      maxDepth: mode === 'tree' ? Math.min(maxDepth, 4) : maxDepth,
      numPaths: mode === 'multi' ? numPaths : undefined,
      branches: mode === 'tree' ? branches : undefined,
      maxTotalPaths: mode === 'tree' ? maxPaths : undefined,
      useWalletExplorer
    };
    
    onModeChange(modeConfig);
  };

  const handleDepthChange = (value) => {
    setMaxDepth(value);
    if (selectedMode !== 'tree' || value <= 4) {
      onModeChange({
        mode: selectedMode,
        maxDepth: value,
        numPaths: selectedMode === 'multi' ? numPaths : undefined,
        branches: selectedMode === 'tree' ? branches : undefined,
        maxTotalPaths: selectedMode === 'tree' ? maxPaths : undefined,
        useWalletExplorer
      });
    }
  };

  const handleWalletExplorerToggle = () => {
    const newValue = !useWalletExplorer;
    setUseWalletExplorer(newValue);
    onModeChange({
      mode: selectedMode,
      maxDepth,
      numPaths: selectedMode === 'multi' ? numPaths : undefined,
      branches: selectedMode === 'tree' ? branches : undefined,
      maxTotalPaths: selectedMode === 'tree' ? maxPaths : undefined,
      useWalletExplorer: newValue
    });
  };

  return (
    <div className="trace-mode-selector">
      <div className="mode-header">
        <h3>🔍 Trace Mode</h3>
        <p className="mode-subtitle">Wähle wie die Transaktion verfolgt werden soll</p>
      </div>

      <div className="mode-grid">
        {modes.map(mode => (
          <div
            key={mode.id}
            className={`mode-card ${selectedMode === mode.id ? 'selected' : ''} ${loading || mode.disabled ? 'disabled' : ''}`}
            onClick={() => !loading && !mode.disabled && handleModeSelect(mode.id)}
            style={selectedMode === mode.id ? { borderColor: mode.color, boxShadow: `0 0 15px ${mode.color}40` } : {}}
            title={mode.disabled ? mode.disabledReason : ''}
          >
            <div className="mode-icon" style={{ color: mode.disabled ? '#999' : mode.color }}>
              {mode.icon}
            </div>
            <div className="mode-info">
              <h4 style={{ color: mode.disabled ? '#999' : 'inherit' }}>
                {mode.name}
                {mode.disabled && <span style={{ fontSize: '0.8em', marginLeft: '5px' }}>🔒</span>}
              </h4>
              <p className="mode-description" style={{ color: mode.disabled ? '#999' : 'inherit' }}>
                {mode.disabled ? mode.disabledReason : mode.description}
              </p>
              <span className="mode-speed" style={{ color: mode.disabled ? '#999' : mode.color }}>
                {mode.speed}
              </span>
            </div>
            {selectedMode === mode.id && !mode.disabled && (
              <div className="mode-check" style={{ color: mode.color }}>✓</div>
            )}
          </div>
        ))}
      </div>

      <div className="mode-settings">
        <div className="setting-group">
          <label>
            <span className="setting-label">Max Depth</span>
            <span className="setting-value">{maxDepth}</span>
          </label>
          <input
            type="range"
            min="1"
            max={selectedMode === 'tree' ? '4' : '15'}
            value={maxDepth}
            onChange={(e) => handleDepthChange(parseInt(e.target.value))}
            disabled={loading}
            className="depth-slider"
          />
          <div className="slider-labels">
            <span>1</span>
            <span>{selectedMode === 'tree' ? '4' : '15'}</span>
          </div>
        </div>

        {selectedMode === 'multi' && (
          <div className="setting-group">
            <label>
              <span className="setting-label">Number of Paths</span>
              <span className="setting-value">{numPaths}</span>
            </label>
            <input
              type="range"
              min="2"
              max="5"
              value={numPaths}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setNumPaths(val);
                onModeChange({ mode: 'multi', maxDepth, numPaths: val });
              }}
              disabled={loading}
              className="depth-slider"
            />
            <div className="slider-labels">
              <span>2</span>
              <span>5</span>
            </div>
          </div>
        )}

        {selectedMode === 'tree' && (
          <>
            <div className="setting-group">
              <label>
                <span className="setting-label">Branches per Level</span>
                <span className="setting-value">{branches}</span>
              </label>
              <input
                type="range"
                min="2"
                max="3"
                value={branches}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setBranches(val);
                  onModeChange({ mode: 'tree', maxDepth: Math.min(maxDepth, 4), branches: val, maxTotalPaths: maxPaths });
                }}
                disabled={loading}
                className="depth-slider"
              />
              <div className="slider-labels">
                <span>2</span>
                <span>3</span>
              </div>
            </div>
            <div className="tree-warning">
              ⚠️ Tree mode kann bis zu {Math.pow(branches, Math.min(maxDepth, 4))} Pfade generieren!
            </div>
          </>
        )}

        {/* WalletExplorer Option */}
        <div className="setting-group wallet-explorer-option">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={useWalletExplorer}
              onChange={handleWalletExplorerToggle}
              disabled={loading}
            />
            <span className="checkbox-custom"></span>
            <div className="checkbox-info">
              <span className="setting-label">🔍 WalletExplorer Lookup</span>
              <span className="setting-description">
                Prüft Adressen automatisch gegen WalletExplorer API (langsamer)
              </span>
            </div>
          </label>
        </div>
      </div>

      {loading && (
        <div className="mode-loading">
          <div className="loading-spinner-small"></div>
          <span>Tracing...</span>
        </div>
      )}
    </div>
  );
}

export default TraceModeSelector;
